'use client'

import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '../../../utils/cn'

import { ProgramCardContentItems } from './a-program-card-content-items'

import type { IProgramItem } from '../_types/types'

const COLLAPSIBLE_MAX_HEIGHT_PX = 366

export interface IProgramCardContentProps {
	items: IProgramItem[]
	programId: number
	/** Label for expand CTA when content is truncated (e.g. "Viac informácií o balíku") */
	expandContentLabel?: string
	/** Label for collapse CTA (e.g. "Menej informácií o balíku") */
	collapseContentLabel?: string
	/** When true, always show full content (no clipping). */
	forceExpand?: boolean
}

export const ProgramCardContent: React.FC<IProgramCardContentProps> = ({
	items,
	programId: _programId,
	expandContentLabel = 'Viac informácií o balíku',
	collapseContentLabel = 'Menej informácií o balíku',
	forceExpand = false
}) => {
	const contentWrapperRef = useRef<HTMLDivElement>(null)
	const [needsExpand, setNeedsExpand] = useState(false)
	const [contentExpanded, setContentExpanded] = useState(false)

	const checkHeight = useCallback(() => {
		if (forceExpand) {
			setNeedsExpand(false)
			return
		}
		const el = contentWrapperRef.current
		if (!el) return
		const height = el.scrollHeight
		setNeedsExpand(height > COLLAPSIBLE_MAX_HEIGHT_PX)
	}, [forceExpand])

	useEffect(() => {
		if (forceExpand) {
			setNeedsExpand(false)
			return
		}
		const el = contentWrapperRef.current
		if (!el) return
		checkHeight()
		const ro = new ResizeObserver(() => {
			checkHeight()
		})
		ro.observe(el)
		return () => ro.disconnect()
	}, [checkHeight, items, forceExpand])

	const showOverlay = needsExpand && !contentExpanded
	const showCollapseButton = needsExpand && contentExpanded

	return (
		<div className='m-program-card__content'>
			<Accordion.Trigger className='m-program-card__content-trigger'>
				<span>Obsahuje</span>
				<ChevronDown
					className='m-program-card__content-trigger-icon'
					size={20}
					aria-hidden
				/>
			</Accordion.Trigger>
			<Accordion.Content className='m-program-card__content-panel'>
				<div className='m-program-card__content-panel-inner'>
					<div
						ref={contentWrapperRef}
						className={cn(
							'm-program-card__content-panel-body',
							showOverlay && 'm-program-card__content-panel-body--collapsed'
						)}
					>
						<ProgramCardContentItems items={items} />
					</div>
					{showOverlay && (
						<>
							<div className='m-program-card__content-panel-overlay' aria-hidden />
							<button
								type='button'
								className='m-program-card__content-panel-expand'
								onClick={() => setContentExpanded(true)}
							>
								<ChevronDown size={20} aria-hidden />
								<span>{expandContentLabel}</span>
							</button>
						</>
					)}
					{showCollapseButton && (
						<button
							type='button'
							className='m-program-card__content-panel-collapse'
							onClick={() => setContentExpanded(false)}
						>
							<ChevronUp size={20} aria-hidden />
							<span>{collapseContentLabel}</span>
						</button>
					)}
				</div>
			</Accordion.Content>
		</div>
	)
}

ProgramCardContent.displayName = 'AProgramCardContent'
